import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { genres } from "@/mocks/genres";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieGrid from "@/components/feature/MovieGrid";
import CategoryFilters from "./components/CategoryFilters";
import Pagination from "./components/Pagination";

const PAGE_SIZE = 8;

const sortOptions = [
  { label: "最新更新", value: "latest" },
  { label: "热度最高", value: "hot" },
  { label: "评分最高", value: "rating" },
];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const genre = genres.find((g) => g.slug === type);
  const genreName = genre ? genre.name : "";

  useDocumentTitle(
    genre ? `${genreName}电影 - 鸭王在线观看` : "电影分类 - 鸭王在线观看",
    genre
      ? `${genreName}电影在线观看，鸭王在线观看为你精选最新${genreName}电影资源，分类清晰、更新及时，随时在线观看。`
      : "鸭王在线观看电影分类页，按类型浏览最新电影资源。"
  );

  const [year, setYear] = useState("all");
  const [region, setRegion] = useState("all");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setYear("all");
    setRegion("all");
    setSort("latest");
    setPage(1);
  }, [type]);

  const baseList = useMemo(
    () => (genre ? movies.filter((m) => m.genres.includes(genre.name)) : []),
    [genre]
  );

  const yearOptions = useMemo(() => {
    const years = Array.from(new Set(baseList.map((m) => m.year))).sort((a, b) => b - a);
    return [{ label: "全部", value: "all" }, ...years.map((y) => ({ label: `${y}`, value: String(y) }))];
  }, [baseList]);

  const regionOptions = useMemo(() => {
    const regions = Array.from(new Set(baseList.map((m) => m.region)));
    return [{ label: "全部", value: "all" }, ...regions.map((r) => ({ label: r, value: r }))];
  }, [baseList]);

  const filtered = useMemo(() => {
    let list = baseList.filter(
      (m) =>
        (year === "all" || String(m.year) === year) &&
        (region === "all" || m.region === region)
    );
    if (sort === "latest") {
      list = [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    } else if (sort === "hot") {
      list = [...list].sort((a, b) => b.views - a.views);
    } else if (sort === "rating") {
      list = [...list].sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [baseList, year, region, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilterChange = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const handleReset = () => {
    setYear("all");
    setRegion("all");
    setSort("latest");
    setPage(1);
  };

  if (!genre) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
          <i className="ri-error-warning-line"></i>
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="text-sm text-foreground-600">你访问的影片分类不存在，去其他分类看看吧。</p>
        <Link
          to="/"
          className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-accent-400"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "电影分类" }, { label: `${genreName}电影` }]} />

      <div className="mt-5 flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
          <i className={`${genre.icon} text-2xl`}></i>
        </span>
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            {genreName}电影
          </h1>
          <p className="mt-1 text-sm text-foreground-600">{genre.desc}</p>
        </div>
      </div>

      <div className="mt-6">
        <CategoryFilters
          years={yearOptions}
          regions={regionOptions}
          sortOptions={sortOptions}
          year={year}
          region={region}
          sort={sort}
          onYearChange={handleFilterChange(setYear)}
          onRegionChange={handleFilterChange(setRegion)}
          onSortChange={handleFilterChange(setSort)}
          onReset={handleReset}
        />
      </div>

      <p className="mt-5 text-sm text-foreground-600">
        共找到 <span className="font-semibold text-accent-500">{filtered.length}</span> 部{genreName}电影
      </p>

      <div className="mt-4">
        {paged.length > 0 ? (
          <MovieGrid movies={paged} />
        ) : (
          <div className="flex flex-col items-center gap-4 rounded-lg border border-background-200/60 bg-background-100 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/60 text-3xl text-foreground-500">
              <i className="ri-inbox-line"></i>
            </span>
            <p className="text-base text-foreground-800">当前分类暂无相关电影</p>
            <p className="text-sm text-foreground-600">试试调整筛选条件，或重置后重新浏览</p>
            <button
              type="button"
              onClick={handleReset}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>

      <Pagination current={page} total={totalPages} onChange={setPage} />
    </div>
  );
}